/* ==========================================================================
   IRON FIT — Machine carousel (phones only)

   The showroom is one unscrollable screen. On a phone that screen holds ONE
   machine at a time and the machines turn on a loop — there is no first and
   no last, so a thumb never hits a wall and never has to swipe back.

   How the loop works, without cloning a single node:

     Each machine is absolutely stacked on the same spot and offset by its
     own signed distance from the centre, d = i - p, WRAPPED into the range
     [-N/2, N/2). At any moment one machine is centred (d = 0), one sits a
     screen to the right (d = +1) and one a screen to the left (d = -1). Push
     p past a machine and its d wraps from +1.5 to -1.5 — a jump that happens
     a screen and a half away, where nothing is visible. Turn p far enough
     and the same three machines come round again, forever.

     Because the positions are periodic in p, p itself can be normalised back
     into [0, N) after every landing with no visible effect at all, so
     nothing drifts however long the carousel runs.

   Motion is tweened here in JS rather than by CSS transitions, because a
   wrapping element must be free to jump: a transition would animate that
   jump across the screen. Everything written is transform and opacity, so a
   phone composites it instead of repainting.

   Above 860px this does nothing and tears itself back out.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__ifzDeck) return;
  window.__ifzDeck = 1;

  var MQ = '(max-width: 860px)';
  var FLICK = 0.45;   /* px/ms — above this a flick pages regardless of distance */
  var CATCH = 0.16;   /* fraction of the screen that counts as a committed drag */
  var GLIDE = 560;    /* ms for a landing */
  var AUTO = 4600;    /* ms between unattended turns */
  var REST = 7000;    /* ms of quiet after a touch before the turning resumes */

  var root = document.documentElement;
  var mq = window.matchMedia(MQ);
  var calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  var showroom, track, cards, dots, hint, prev, next;
  var live = false;
  var N = 0, p = 0, index = 0;

  /* motion */
  var anim = 0, guard = 0;

  /* drag */
  var down = false, dragging = false, swiped = false, swipedAt = 0;
  var x0 = 0, y0 = 0, p0 = 0, xPrev = 0, tPrev = 0, vel = 0, frame = 0;

  /* auto-turn */
  var turner = 0, touchedAt = 0;

  function nowMs() {
    return (window.performance && performance.now) ? performance.now() : +new Date();
  }

  /* ---------------------------------------------------------------------
     Geometry
     --------------------------------------------------------------------- */

  /* Signed distance from the centre, folded into [-N/2, N/2). This one
     function is the whole loop. */
  function wrap(v) {
    var h = N / 2;
    return ((v + h) % N + N) % N - h;
  }

  function paint() {
    for (var i = 0; i < N; i++) {
      var d = wrap(i - p);
      var a = Math.min(Math.abs(d), 1);
      var s = cards[i].style;
      s.setProperty('--d', d.toFixed(4));
      s.setProperty('--s', (1 - a * 0.16).toFixed(4));   /* neighbours sit back */
      s.setProperty('--o', (1 - a * 0.78).toFixed(4));   /* and sink into the dark */
    }
    /* Periodic, so the backdrop drifts forever without ever jumping when a
       machine wraps. */
    showroom.style.setProperty('--bx', (Math.sin(p / N * Math.PI * 2) * 2.4).toFixed(3) + '%');
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(function () { frame = 0; paint(); });
  }

  function stopAnim() {
    if (anim) { cancelAnimationFrame(anim); anim = 0; }
    if (guard) { clearTimeout(guard); guard = 0; }
  }

  function settle(target) {
    p = ((target % N) + N) % N;      /* normalise — invisible, and never drifts */
    index = Math.round(p) % N;
    paint();
    markDots();
  }

  function glide(target) {
    stopAnim();

    if (calm.matches) { settle(target); return; }

    var from = p, delta = target - from, t0 = nowMs();

    anim = requestAnimationFrame(function step() {
      var k = Math.min(1, (nowMs() - t0) / GLIDE);
      var e = 1 - Math.pow(1 - k, 3);          /* ease out, lands soft */
      p = from + delta * e;
      paint();
      if (k < 1) anim = requestAnimationFrame(step);
      else { stopAnim(); settle(target); }
    });

    /* A tab that never runs a frame callback must still end up in the right
       place — the carousel may not animate, but it may never be left adrift. */
    guard = setTimeout(function () {
      if (anim) { stopAnim(); settle(target); }
    }, GLIDE + 400);
  }

  function turnTo(i) {
    /* i is unbounded on purpose: +1 from the last machine is the first one,
       approached from the right, exactly as a loop should feel. */
    wakeAll();
    glide(i);
    index = ((Math.round(i) % N) + N) % N;
    markDots();
  }

  function markDots() {
    if (!dots) return;
    for (var i = 0; i < dots.children.length; i++) {
      var on = i === index;
      dots.children[i].classList.toggle('is-on', on);
      dots.children[i].setAttribute('aria-current', on ? 'true' : 'false');
    }
  }

  /* A machine's copy must already be standing by the time it comes round. */
  function wake(i) { if (cards[i]) cards[i].classList.add('is-live'); }
  function wakeAll() { for (var i = 0; i < N; i++) wake(i); }

  /* ---------------------------------------------------------------------
     Turning on its own
     --------------------------------------------------------------------- */

  function held() {
    return document.hidden
        || down
        || root.classList.contains('pk-open')
        || root.classList.contains('ifs-open')
        || !!document.querySelector('.mch.is-open')      /* reading a spec sheet */
        || (Date.now() - touchedAt) < REST;              /* just been handled */
  }

  function autoOn() {
    if (calm.matches) return;
    clearInterval(turner);
    turner = setInterval(function () {
      if (!live || held()) return;
      glide(p + 1);
      index = ((Math.round(p + 1) % N) + N) % N;
      markDots();
    }, AUTO);
  }

  function autoOff() { clearInterval(turner); turner = 0; }

  function touched() {
    touchedAt = Date.now();
    showroom.classList.add('is-touched');    /* the swipe hint retires */
  }

  /* ---------------------------------------------------------------------
     Drag
     --------------------------------------------------------------------- */

  function onDown(e) {
    /* Cleared FIRST, unconditionally. `swiped` is what tells the capture
       handler to suppress the click a drag would otherwise fire, and a drag
       on a touchscreen does not reliably produce that click — so the flag can
       outlive its drag. Any new press is a new interaction. */
    down = false;
    dragging = false;
    swiped = false;

    if (e.pointerType === 'mouse' && e.button !== 0) return;
    /* The spec sheet and the dots handle their own presses. */
    if (e.target.closest('.spec') || e.target.closest('.deck__dot')) return;

    down = true;
    x0 = xPrev = e.clientX;
    y0 = e.clientY;
    tPrev = e.timeStamp;
    vel = 0;
    touched();
    stopAnim();
    p0 = Math.round(p);        /* landings are always whole, so this is exact */
    p = p0;
  }

  function onMove(e) {
    if (!down) return;

    var dx = e.clientX - x0;
    var dy = e.clientY - y0;

    /* Commit to the horizontal axis before hijacking anything. */
    if (!dragging) {
      if (Math.abs(dx) < 6) return;
      if (Math.abs(dx) < Math.abs(dy)) { down = false; return; }
      dragging = true;
      swiped = true;
      wakeAll();
      try { track.setPointerCapture(e.pointerId); } catch (err) {}
    }

    if (e.cancelable) e.preventDefault();

    var dt = e.timeStamp - tPrev;
    if (dt > 0) {
      vel = (e.clientX - xPrev) / dt;
      xPrev = e.clientX;
      tPrev = e.timeStamp;
    }

    /* No clamp and no rubber band: there is no end to reach. */
    p = p0 - dx / (showroom.clientWidth || 1);
    schedule();
  }

  function onUp() {
    if (!down) return;
    down = false;
    touched();

    if (!dragging) return;
    dragging = false;
    swipedAt = Date.now();

    var travel = p - p0;
    var whole = travel < 0 ? Math.ceil(travel) : Math.floor(travel);
    var rest = travel - whole;
    var dir = 0;

    if (Math.abs(vel) > FLICK) dir = vel < 0 ? 1 : -1;
    else if (Math.abs(rest) > CATCH) dir = rest > 0 ? 1 : -1;

    turnTo(p0 + whole + dir);
  }

  /* A drag must never also count as a tap on the machine underneath. The
     showroom's own handler navigates on click, so this runs in the capture
     phase and cuts the click off before it reaches it.

     The window matters. The click being suppressed is the one the browser
     synthesises at the end of a drag, which lands immediately. Suppressing on
     the flag alone would also eat the NEXT deliberate activation whenever no
     such click ever arrived — and one that comes from the keyboard, with no
     pointerdown ahead of it to clear the flag, would be swallowed outright. */
  function onClickCapture(e) {
    if (!swiped) return;
    swiped = false;
    if (Date.now() - swipedAt > 350) return;
    e.stopPropagation();
    e.preventDefault();
  }

  /* ---------------------------------------------------------------------
     Chrome
     --------------------------------------------------------------------- */

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function buildChrome() {
    dots = el('div', 'deck__dots');
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Machines');

    cards.forEach(function (c, i) {
      var name = c.querySelector('.mch__name');
      var b = el('button', 'deck__dot');
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', name ? name.textContent.replace(/\s+/g, ' ').trim() : 'Machine ' + (i + 1));
      b.addEventListener('click', function () {
        touched();
        /* Reach it the short way round, so tapping the last dot from the
           first machine turns backwards rather than all the way forward. */
        turnTo(Math.round(p) + wrap(i - Math.round(p)));
      });
      dots.appendChild(b);
    });
    showroom.appendChild(dots);

    prev = el('button', 'deck__arrow deck__arrow--prev', '<i aria-hidden="true">&#8249;</i>');
    next = el('button', 'deck__arrow deck__arrow--next', '<i aria-hidden="true">&#8250;</i>');
    prev.type = next.type = 'button';
    prev.setAttribute('aria-label', 'Previous machine');
    next.setAttribute('aria-label', 'Next machine');
    prev.addEventListener('click', function () { touched(); turnTo(Math.round(p) - 1); });
    next.addEventListener('click', function () { touched(); turnTo(Math.round(p) + 1); });
    showroom.appendChild(prev);
    showroom.appendChild(next);
    /* No swipe label. It had nowhere to sit that was not on top of the call
       to action, and a carousel that turns on its own teaches the gesture
       better than any caption. */
  }

  function removeChrome() {
    [dots, prev, next, hint].forEach(function (n) { if (n && n.parentNode) n.remove(); });
    dots = prev = next = hint = null;
  }

  /* ---------------------------------------------------------------------
     Entrance — choreographed against the loading gate, not timed to guess it
     --------------------------------------------------------------------- */

  function lightUp() {
    if (!live) return;
    showroom.classList.add('is-lit');   /* backdrop sinks, chrome fades in */
    wake(0);                            /* first machine builds itself up */

    /* Once it has finished, quietly stand the others up off-screen and let
       the carousel start turning. */
    setTimeout(function () { wakeAll(); autoOn(); }, 1150);
  }

  /* ---------------------------------------------------------------------
     Wiring
     --------------------------------------------------------------------- */

  function enable() {
    if (live) return;
    live = true;

    root.classList.add('ifz-deck');
    p = 0;
    index = 0;
    paint();

    buildChrome();
    markDots();

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove, { passive: false });
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
    showroom.addEventListener('click', onClickCapture, true);

    if (window.__ifzRevealed) lightUp();
  }

  function disable() {
    if (!live) return;
    live = false;

    stopAnim();
    autoOff();

    root.classList.remove('ifz-deck');
    showroom.classList.remove('is-lit', 'is-touched');
    showroom.style.removeProperty('--bx');
    track.style.transform = '';
    cards.forEach(function (c) {
      c.style.removeProperty('--d');
      c.style.removeProperty('--s');
      c.style.removeProperty('--o');
      c.classList.remove('is-live');
    });

    removeChrome();

    track.removeEventListener('pointerdown', onDown);
    track.removeEventListener('pointermove', onMove);
    track.removeEventListener('pointerup', onUp);
    track.removeEventListener('pointercancel', onUp);
    showroom.removeEventListener('click', onClickCapture, true);
  }

  function sync() {
    if (mq.matches) enable(); else disable();
  }

  function init() {
    showroom = document.querySelector('.showroom');
    track = document.querySelector('.show__cols');
    cards = [].slice.call(document.querySelectorAll('.mch'));
    if (!showroom || !track || cards.length < 2) return;

    N = cards.length;

    sync();

    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);

    window.addEventListener('ifz:reveal', lightUp);

    /* Nothing should turn in a tab nobody is looking at. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAnim();
    });

    document.addEventListener('keydown', function (e) {
      if (!live) return;
      if (document.querySelector('.ifs.is-open') || root.classList.contains('pk-open')) return;
      if (e.key === 'ArrowRight') { touched(); turnTo(Math.round(p) + 1); }
      else if (e.key === 'ArrowLeft') { touched(); turnTo(Math.round(p) - 1); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
